/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray/Control types require any for array field names */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { nudgeApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type { Nudge, NudgeRequest, NudgeCategory, NudgeType, Priority, Status, DeliveryFrequency, GenerationSource, SkillLevel } from '@/types/learningManagement';

const triggerConditionSchema = z.object({
  conditionType: z.string().min(1, 'Condition type is required'),
  operator: z.string().min(1, 'Operator is required'),
  value: z.string().min(1, 'Value is required'),
  parameters: z.record(z.string(), z.unknown()).optional(),
});

const nudgeSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  content: z.string().min(1, 'Content is required'),
  skillId: z.string().optional(),
  nudgeType: z.enum(['TECHNICAL', 'SOFT_SKILL', 'BEHAVIORAL', 'PRODUCTIVITY', 'WELLNESS', 'MOTIVATIONAL', 'CUSTOM']),
  category: z.enum(['REMINDER', 'MOTIVATION', 'HABIT_BUILDING', 'PROGRESS_CHECK', 'GOAL_ALIGNMENT', 'SKILL_DEVELOPMENT', 'LEARNING_PATH', 'ASSESSMENT_PREP', 'REFLECTION', 'CELEBRATION']),
  subCategory: z.string().max(100).optional(),
  tags: z.array(z.string().max(50)).optional(),
  priority: z.enum(['LOW', 'MEDIUM', 'HIGH', 'URGENT']),
  personalizationParams: z.record(z.string(), z.unknown()).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  description: z.string().optional(),
  status: z.enum(['READ', 'DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED', 'EXPIRED', 'ARCHIVED']),
  targetAudiences: z.array(z.string()),
  triggerConditions: z.array(triggerConditionSchema),
  actionItems: z.array(z.string()),
  skillLevel: z.enum(['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY']).optional(),
  deliveryFrequency: z.enum(['ONCE', 'DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'ON_TRIGGER', 'CUSTOM']).optional(),
  deliveryChannels: z.array(z.enum(['PUSH_NOTIFICATION', 'EMAIL', 'IN_APP_MESSAGE', 'SMS'])),
  successMetrics: z.array(z.string()),
  nudgeGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
});

type NudgeFormData = z.infer<typeof nudgeSchema>;

const CATEGORIES: NudgeCategory[] = ['REMINDER', 'MOTIVATION', 'HABIT_BUILDING', 'PROGRESS_CHECK', 'GOAL_ALIGNMENT', 'SKILL_DEVELOPMENT', 'LEARNING_PATH', 'ASSESSMENT_PREP', 'REFLECTION', 'CELEBRATION'];
const NUDGE_TYPES: NudgeType[] = ['TECHNICAL', 'SOFT_SKILL', 'BEHAVIORAL', 'PRODUCTIVITY', 'WELLNESS', 'MOTIVATIONAL', 'CUSTOM'];
const PRIORITIES: Priority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const STATUSES: Status[] = ['READ', 'DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED', 'EXPIRED', 'ARCHIVED'];
const DELIVERY_FREQUENCIES: DeliveryFrequency[] = ['ONCE', 'DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'ON_TRIGGER', 'CUSTOM'];
const SKILL_LEVELS: SkillLevel[] = ['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];

export const NudgeFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Array<{ id?: string; name: string }>>([]);
  const { hasPermission } = useAdminAuth();

  const form = useForm<NudgeFormData>({
    resolver: zodResolver(nudgeSchema) as any,
    defaultValues: {
      title: '',
      content: '',
      skillId: '',
      nudgeType: 'TECHNICAL',
      category: 'REMINDER',
      subCategory: '',
      priority: 'MEDIUM',
      status: 'DRAFT',
      description: '',
      targetAudiences: [],
      triggerConditions: [],
      actionItems: [],
      skillLevel: undefined,
      deliveryFrequency: 'ONCE',
      deliveryChannels: [],
      successMetrics: [],
      personalizationParams: undefined,
      metadata: undefined,
      tags: [],
      nudgeGenerated: 'USER_CUSTOMIZED',
    },
  });

  const { fields: audienceFields, append: appendAudience, remove: removeAudience } = useFieldArray({
    control: form.control as any,
    name: 'targetAudiences' as any,
  });


  const { fields: actionFields, append: appendAction, remove: removeAction } = useFieldArray({
    control: form.control as any,
    name: 'actionItems' as any,
  });

  const { fields: metricFields, append: appendMetric, remove: removeMetric } = useFieldArray({
    control: form.control as any,
    name: 'successMetrics' as any,
  });

  useEffect(() => {
    // Load skills for dropdown
    skillApi.list({ page: 0, size: 1000 }).then((response) => {
      setSkills((response.content || []).map((s) => ({ id: s.id, name: s.name })));
    }).catch((error) => {
      console.error('Error loading skills:', error);
    });
  }, []);

  // Fetch nudge data for edit mode
  const {
    data: _nudge,
    isLoading: isLoadingNudge,
    error: nudgeError,
  } = useEntity<Nudge>({
    fetchFn: (nudgeId: string) => nudgeApi.getById(nudgeId),
    entityId: id,
    entityName: 'nudge',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        content: data.content,
        skillId: data.skillId || '',
        nudgeType: data.nudgeType,
        category: data.category,
        subCategory: data.subCategory || '',
        priority: data.priority,
        status: data.status as NudgeFormData['status'],
        description: data.description || '',
        targetAudiences: data.targetAudiences || [],
        triggerConditions: data.triggerConditions || [],
        actionItems: data.actionItems || [],
        skillLevel: data.skillLevel,
        deliveryFrequency: data.deliveryFrequency,
        deliveryChannels: data.deliveryChannels || [],
        successMetrics: data.successMetrics || [],
        personalizationParams: data.personalizationParams,
        metadata: data.metadata,
        tags: data.tags || [],
        nudgeGenerated: data.nudgeGenerated,
      } as NudgeFormData);
    },
    onError: () => {
      navigate('/admin/nudges');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: NudgeFormData) => {
      const payload: NudgeRequest = {
        ...data,
        tags: data.tags || [],
        deliveryChannels: data.deliveryChannels || [],
      };
      return await nudgeApi.create(payload);
    },
    entityName: 'nudge',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/nudges');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: NudgeFormData) => {
      if (!id) throw new Error('Nudge ID is required');
      const payload: NudgeRequest = {
        ...data,
        tags: data.tags || [],
        deliveryChannels: data.deliveryChannels || [],
      };
      return await nudgeApi.update(id, payload);
    },
    entityName: 'nudge',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/nudges');
    },
  });

  const onSubmit = async (data: NudgeFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingNudge;
  const mutation = isEdit ? updateMutation : createMutation;

  const isSystemDefined = form.watch('nudgeGenerated') === 'SYSTEM_DEFINED';
  const canModifyGeneration = !isEdit || !isSystemDefined || hasPermission('system_admin', 'nudge');

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading nudge..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && nudgeError) {
    return <ErrorState error={nudgeError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/nudges">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Nudge' : 'Create Nudge'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update nudge information' : 'Create a new nudge'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the nudge</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter nudge title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter nudge description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter nudge content"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="skillId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Skill ID</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(value === 'none' ? undefined : value)} 
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select skill (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {skills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id || ''}>
                            {skill.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Select the skill this nudge is associated with (optional)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nudgeType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nudge Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select nudge type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {NUDGE_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PRIORITIES.map((priority) => (
                            <SelectItem key={priority} value={priority}>
                              {priority}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="subCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sub Category</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter sub category (optional)" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="skillLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Skill Level</FormLabel>
                      <Select 
                        onValueChange={(value) => field.onChange(value === 'none' ? undefined : value)} 
                        value={field.value || 'none'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select skill level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {SKILL_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="deliveryFrequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Delivery Frequency</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DELIVERY_FREQUENCIES.map((freq) => (
                          <SelectItem key={freq} value={freq}>
                            {freq.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <div>
                  <Label>Target Audiences</Label>
                  <div className="space-y-2 mt-2">
                    {audienceFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter target audience"
                          value={form.watch(`targetAudiences.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('targetAudiences') || [];
                            current[index] = e.target.value;
                            form.setValue('targetAudiences', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeAudience(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendAudience('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Target Audience
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Action Items</Label>
                  <div className="space-y-2 mt-2">
                    {actionFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter action item"
                          value={form.watch(`actionItems.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('actionItems') || [];
                            current[index] = e.target.value;
                            form.setValue('actionItems', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeAction(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendAction('' as any)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Action Item
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Success Metrics</Label>
                  <div className="space-y-2 mt-2">
                    {metricFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter success metric"
                          value={form.watch(`successMetrics.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('successMetrics') || [];
                            current[index] = e.target.value;
                            form.setValue('successMetrics', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeMetric(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendMetric('' as any)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Success Metric
                    </Button>
                  </div>
                </div>
              </div>

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter tags separated by commas"
                        value={field.value?.join(', ') || ''}
                        onChange={(e) => {
                          const tags = e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean);
                          field.onChange(tags);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nudgeGenerated"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generation Source *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={!canModifyGeneration}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select generation source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GENERATION_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {!canModifyGeneration && 'Cannot change generation source for system-defined nudges'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/nudges">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default NudgeFormPage;
